import type { MechanicDefinition } from "./types";

export const influence: MechanicDefinition = {
  id: "influence",
  name: "Influence & Politics",
  description: "Soft-power layer for diplomacy, blackmail, alliances, betrayal arcs, and political maneuvering",
  icon: "Crown",
  category: "narrative",
  aiHints: {
    purpose: "Adds political intrigue — alliances, blackmail, betrayals, and diplomatic maneuvering as gameplay.",
    whenToUse: "Political games, court intrigue, spy thrillers, or any world with power dynamics.",
    synergies: ["reputation", "morality", "organization", "dialogue_system"],
    conflicts: [],
    prerequisites: ["Faction entities recommended"],
    tags: ["politics", "diplomacy", "intrigue", "alliances", "power"],
    complexity: 2,
    exampleConfig: { depth: "deep", betrayal_enabled: true },
    stateVariables: [],
  },
  configSchema: [
    {
      key: "political_landscape",
      label: "Political Landscape",
      type: "select",
      options: [
        { value: "underworld", label: "Underworld", description: "Crime bosses, gangs, corrupt officials — power through fear and favors" },
        { value: "corporate", label: "Corporate", description: "Board rooms, shareholders, lobbyists — power through money and leverage" },
        { value: "government", label: "Government", description: "Politicians, bureaucrats, military — power through authority and law" },
        { value: "feudal", label: "Feudal", description: "Lords, vassals, clergy — power through land, blood, and faith" },
      ],
      default: "underworld",
    },
    {
      key: "leverage_system",
      label: "Leverage & Blackmail",
      type: "boolean",
      default: true,
      description: "Players can discover and use secrets, dirt, and leverage against NPCs and factions",
    },
    {
      key: "alliance_mechanics",
      label: "Alliance Mechanics",
      type: "boolean",
      default: true,
      description: "Formal pacts, treaties, and alliances with benefits and obligations",
    },
  ],
  systemPromptSection: (config) => {
    const landscape = config.political_landscape || "underworld";
    const leverage = config.leverage_system !== false;
    const alliances = config.alliance_mechanics !== false;

    const landscapeDesc: Record<string, string> = {
      underworld: "Underworld politics — crime bosses negotiate through intermediaries, deals are sealed with blood oaths, betrayal is punished brutally. Respect and fear are currency.",
      corporate: "Corporate politics — hostile takeovers, golden parachutes, proxy wars. Information is leverage, reputation is everything, every handshake hides a knife.",
      government: "Government politics — bureaucratic maneuvering, campaign financing, intelligence agencies. Laws can be weaponized, allies can be subpoenaed, scandals destroy careers.",
      feudal: "Feudal politics — sworn oaths, marriage alliances, succession crises. Honor matters (or at least appearing honorable). Betrayal brings war.",
    };

    return `## INFLUENCE & POLITICS (Guidelines)
- Political landscape: ${landscapeDesc[landscape]}

### POLITICAL DYNAMICS
- Every significant NPC and faction has POLITICAL INTERESTS — what they want, what they fear, who they oppose.
- When the player interacts with power structures, present POLITICAL OPPORTUNITIES:
  - Who can be turned into an ally? What's the price?
  - Who is vulnerable? What are their weaknesses?
  - What alliances exist that could be disrupted?
  - What power vacuums exist that could be exploited?
- Political actions have CASCADING CONSEQUENCES — helping one faction may alienate another.
- Extend the Reputation mechanic with political context — reputation IS political capital.
${leverage ? `
### LEVERAGE & BLACKMAIL
- NPCs have SECRETS that can be discovered through investigation, espionage, or social interaction.
- Leverage can be used to:
  - Force cooperation without combat
  - Extort resources or favors
  - Prevent betrayal (mutually assured destruction)
  - Gain access to restricted areas/information
- Using leverage has RISKS: the target may retaliate, hire assassins, or expose the player's own secrets.
- Track known leverage in the narrative — reference it when the player interacts with that NPC.
- Some leverage expires (time-sensitive secrets) or can be countered (target gets ahead of the story).` : ""}
${alliances ? `
### ALLIANCES & PACTS
- Formal alliances can be proposed between the player's organization and NPC factions.
- Alliance types:
  - Non-aggression pact: we won't attack each other
  - Mutual defense: attack one, fight both
  - Trade agreement: shared resources at favorable rates
  - Full alliance: shared territory, joint operations, combined forces
- Alliances have OBLIGATIONS — failure to honor them damages reputation severely.
- Alliances can be BETRAYED — but betrayal makes future alliances harder to form.
- NPCs may propose alliances when it benefits them — often with hidden agendas.
- Alliance strength depends on trust, mutual benefit, and external threats.` : ""}

### POLITICAL EVENTS
- Regularly introduce political events that create opportunities and threats:
  - Power vacuum: a faction leader is eliminated — who fills the void?
  - Betrayal: an ally is secretly working with a rival
  - Election/succession: a change in leadership changes the political landscape
  - Scandal: embarrassing information becomes public
  - War: conflict between factions forces the player to choose sides`;
  },
  widgets: [],
  initializeState: () => ({}),
  tools: [],
};
